import { createContext, Dispatch, SetStateAction, useContext, FC, useState, useEffect } from 'react'
import { WithChildren } from '../models/withChildren'
import { AppConstantes } from '../utils/Constantes'
import { useDispatch } from 'react-redux'
import {
  cerrarSesion,
  setTokens,
  setUsuario,
} from '../redux/slices/autenticacion/autenticacion.slice'
import { useRecuperarDatosUsuarioMutMutation } from '../redux/api/autenticacion.api'
import { getItemLocalStorageByKey } from '../utils/StorageUtil'
import { useLocation, useNavigate } from 'react-router-dom'

interface AuthModel {}

interface UsuarioModel {}

type AuthContextProps = {
  auth: AuthModel | undefined
  guardarAutorizacion: (auth: AuthModel | undefined) => void
  usuario: UsuarioModel | undefined
  setUsuario: Dispatch<SetStateAction<UsuarioModel | undefined>>
  cerrarSesion: () => void
}

const AutorizacionContextPropsState = {
  auth: () => {},
  guardarAutorizacion: () => {},
  usuario: undefined,
  setUsuario: () => {},
  cerrarSesion: () => {},
}

export const AutenticacionContext = createContext<AuthContextProps>(AutorizacionContextPropsState)

const useAutenticacion = () => {
  return useContext(AutenticacionContext)
}

const AutenticacionProvider: FC<WithChildren> = ({ children }) => {
  const [auth, setAutorizacion] = useState<AuthModel | undefined>()
  const [usuario, setUsuario] = useState<UsuarioModel | undefined>()

  const guardarAutorizacion = (auth: AuthModel | undefined) => {
    setAutorizacion(auth)
  }

  const cerrarSesion = () => {
    guardarAutorizacion(undefined)
    setUsuario(undefined)
  }

  return (
    <AutenticacionContext.Provider
      value={{ auth, guardarAutorizacion, usuario, setUsuario, cerrarSesion }}>
      {children}
    </AutenticacionContext.Provider>
  )
}

/**
 * Restaura la sesion al cargar la aplicacion.
 *
 * Si existen tokens en localStorage, los sube a Redux y consulta `/u` para
 * recuperar el usuario actual con sus roles. Si no hay tokens, limpia la sesion
 * para que `AppRoutes` mantenga al usuario en las rutas publicas.
 */
const AutorizacionInit: FC<WithChildren> = ({ children }) => {
  const dispatch = useDispatch()
  const location = useLocation()
  const navigate = useNavigate()

  const [showSplashScreen, setShowSplashScreen] = useState(true)

  const [recuperarDatosUsuario] = useRecuperarDatosUsuarioMutMutation()

  useEffect(() => {
    const accessToken = getItemLocalStorageByKey(AppConstantes.ACCESS_TOKEN)
    const refreshToken = getItemLocalStorageByKey(AppConstantes.REFRESH_TOKEN)

    if (accessToken && refreshToken) {
      dispatch(setTokens({ accessToken, refreshToken }))
      recuperarDatosUsuario(null)
        .unwrap()
        .then((resp) => {
          setShowSplashScreen(false)
          dispatch(setUsuario(resp))
          navigate(location.pathname)
        })
        .catch((error) => {
          console.error(error)
          setShowSplashScreen(false)
        })
    } else {
      setShowSplashScreen(false)
      dispatch(cerrarSesion())
    }
  }, [])

  // We should request user by authToken (IN OUR EXAMPLE IT'S API_TOKEN) before rendering the application
  // useEffect(() => {
  // const requestUser = async (apiToken: string) => {
  // try {
  //     if (!didRequest.current) {
  //     const {data} = await getUserByToken(apiToken)
  //     if (data) {
  //         setCurrentUser(data)
  //     }
  //     }
  // } catch (error) {
  //     console.error(error)
  //     if (!didRequest.current) {
  //     logout()
  //     }
  // } finally {
  //     setShowSplashScreen(false)
  // }

  // return () => (didRequest.current = true)
  // }

  // if (auth && auth.api_token) {
  // requestUser(auth.api_token)
  // } else {
  // logout()
  // setShowSplashScreen(false)
  // }
  // // eslint-disable-next-line
  // }, [])

  return showSplashScreen ? <></> : <>{children}</>
}

export { AutenticacionProvider, useAutenticacion, AutorizacionInit }
